import { useRef, useState } from 'react';
import { Button } from './ui/button';
import { Camera, ImagePlus, Loader2, RefreshCw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const MAX_SOURCE_BYTES = 8 * 1024 * 1024;
const MAX_OUTPUT_BYTES = 3 * 1024 * 1024;
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

async function resizeReceipt(file) {
  if (!ALLOWED_TYPES.includes(file.type)) throw new Error('Usá una imagen JPG, PNG o WebP');
  if (file.size > MAX_SOURCE_BYTES) throw new Error('La imagen original no puede superar 8 MB');

  const objectUrl = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.src = objectUrl;
    await image.decode();
    const scale = Math.min(1, 1600 / Math.max(image.width, image.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.width * scale));
    canvas.height = Math.max(1, Math.round(image.height * scale));
    canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.82);
    const outputBytes = Math.ceil((dataUrl.length - dataUrl.indexOf(',') - 1) * 0.75);
    if (outputBytes > MAX_OUTPUT_BYTES) throw new Error('No se pudo reducir la imagen por debajo de 3 MB');
    return dataUrl;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export default function ReceiptImageInput({ value, onChange, disabled = false }) {
  const cameraInput = useRef(null);
  const galleryInput = useRef(null);
  const [processing, setProcessing] = useState(false);

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setProcessing(true);
    try {
      onChange(await resizeReceipt(file));
    } catch (error) {
      toast.error(error.message || 'No se pudo procesar el comprobante');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-2">
      <input ref={cameraInput} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" onChange={handleFile} className="hidden" />
      <input ref={galleryInput} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFile} className="hidden" />

      {value ? (
        <div className="overflow-hidden rounded-lg border border-slate-600 bg-slate-900/50">
          <img src={value} alt="Vista previa del comprobante" className="max-h-56 w-full object-contain" />
          <div className="grid grid-cols-2 gap-2 p-2">
            <Button type="button" size="sm" variant="outline" disabled={disabled || processing} onClick={() => galleryInput.current?.click()} className="border-slate-600 text-slate-300">
              <RefreshCw className="mr-1 h-4 w-4" />Reemplazar
            </Button>
            <Button type="button" size="sm" variant="ghost" disabled={disabled || processing} onClick={() => onChange(null)} className="text-red-400">
              <Trash2 className="mr-1 h-4 w-4" />Eliminar
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <Button type="button" size="sm" variant="outline" disabled={disabled || processing} onClick={() => cameraInput.current?.click()} className="border-slate-600 text-slate-300">
            {processing ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Camera className="mr-1 h-4 w-4" />}
            Sacar foto
          </Button>
          <Button type="button" size="sm" variant="outline" disabled={disabled || processing} onClick={() => galleryInput.current?.click()} className="border-slate-600 text-slate-300">
            <ImagePlus className="mr-1 h-4 w-4" />Galería / archivo
          </Button>
        </div>
      )}
      <p className="text-[11px] text-slate-500">JPG, PNG o WebP. La imagen se optimiza antes de subir (máximo 3 MB).</p>
    </div>
  );
}
